import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import CloseIcon from '../../assets/icons/close.svg?react';
import { Button } from '../Button';
import { logger } from '../../utils/logger';

export interface ErrorDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  errorDetails: string;
}

/**
 * ErrorDetailsDialog - Shows detailed error information in a formatted code block
 * with a copy-to-clipboard button
 */
export function ErrorDetailsDialog({
  open,
  onOpenChange,
  title,
  errorDetails,
}: ErrorDetailsDialogProps) {
  const [copied, setCopied] = useState(false);

  // Try to format JSON for better readability
  const formattedDetails = (() => {
    try {
      const parsed = JSON.parse(errorDetails);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return errorDetails;
    }
  })();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(formattedDetails);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logger.error('Failed to copy to clipboard:', err);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        {/* Overlay */}
        <Dialog.Overlay className='fixed inset-0 z-50 bg-black/50' />

        {/* Dialog Content */}
        <Dialog.Content
          className='bg-surface-primary fixed top-1/2 left-1/2 z-50 flex
            max-h-[85vh] w-full max-w-2xl -translate-x-1/2 -translate-y-1/2
            flex-col overflow-hidden rounded-xl
            shadow-[0px_12px_24px_0px_rgba(0,0,0,0.1)]'
          aria-describedby='error-details-description'
        >
          {/* Header */}
          <div
            className='border-border-secondary flex shrink-0 items-center
              justify-between border-b p-6'
          >
            <div className='flex flex-col gap-1'>
              <Dialog.Title className='size-h4 text-primary font-bold'>
                {title}
              </Dialog.Title>
              <Dialog.Description
                id='error-details-description'
                className='size-caption text-primary'
              >
                Technical details for debugging purposes
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                className='text-secondary hover:text-primary rounded-sm p-2
                  transition-colors'
                aria-label='Close'
              >
                <CloseIcon className='h-4 w-4' />
              </button>
            </Dialog.Close>
          </div>

          {/* Content */}
          <div className='min-h-0 flex-1 overflow-hidden p-6'>
            <div className='relative h-full'>
              {/* Copy button */}
              <button
                onClick={handleCopy}
                className='absolute top-2 right-4 z-10 rounded bg-gray-700 px-2
                  py-1 text-xs text-white transition-colors hover:bg-gray-600'
                aria-label='Copy error details to clipboard'
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>

              {/* Code block with dark scrollbar */}
              <pre
                className='max-h-[50vh] overflow-auto rounded-lg bg-gray-900 p-4
                  pt-10 text-sm text-gray-100 [&::-webkit-scrollbar]:w-2
                  [&::-webkit-scrollbar-thumb]:rounded-full
                  [&::-webkit-scrollbar-thumb]:bg-gray-600
                  [&::-webkit-scrollbar-track]:bg-gray-800'
                style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#4b5563 #1f2937',
                }}
              >
                <code className='wrap-break-word whitespace-pre-wrap'>
                  {formattedDetails}
                </code>
              </pre>
            </div>
          </div>

          {/* Footer */}
          <div
            className='bg-surface-primary-support border-border-primary flex
              shrink-0 items-center justify-end border-t px-6 py-4'
          >
            <Dialog.Close asChild>
              <Button variant='secondary'>Close</Button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
